import { Button } from "@/components/ui/button";
import { ShoppingCart, TrendingDown, Plus, Minus } from "lucide-react";
import { useSuppressAdsWhile } from "@/hooks/useShowAds";

interface SelectedItem {
  productId: string;
  productName: string;
  quantity: number;
  rate: number;
}

interface BillingBarProps {
  selectedItems: SelectedItem[];
  onSell: () => void;
  onBuy?: () => void;
  onSellAndShare?: () => void;
  isProcessing?: boolean;
  discount?: number;
  userMode?: string;
  onQuantityChange?: (productId: string, productName: string, rate: number, quantity: number) => void;
  lastTappedProductId?: string | null;
}

export function BillingBar({
  selectedItems,
  onSell,
  onBuy,
  onSellAndShare,
  isProcessing,
  discount = 0,
  userMode,
  onQuantityChange,
  lastTappedProductId,
}: BillingBarProps) {
  const totalAmount = selectedItems.reduce(
    (sum, item) => sum + item.quantity * item.rate,
    0,
  ) - discount;
  const totalItems = selectedItems.reduce(
    (sum, item) => sum + item.quantity,
    0,
  );

  // Hide the native ad banner while the cart is active so it never covers Sell.
  useSuppressAdsWhile(selectedItems.length > 0);

  if (selectedItems.length === 0) {
    return null;
  }

  const isPro = userMode === "pro";

  return (
    <div className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] bg-card border-t border-border shadow-lg z-40 p-4">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm text-muted-foreground">
              {totalItems} item{totalItems > 1 ? "s" : ""} selected
            </p>
            <p className="text-xl font-bold">₹{totalAmount.toFixed(2)}</p>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex gap-2 items-center">
              {isPro ? (
                <div className="flex items-center gap-3 bg-secondary/50 rounded-xl p-1 px-3">
                  {(() => {
                    const itemToControl = lastTappedProductId
                      ? selectedItems.find(item => item.productId === lastTappedProductId)
                      : selectedItems[selectedItems.length - 1];

                    if (!itemToControl) return null;
                    return (
                      <div key={itemToControl.productId} className="flex items-center gap-2">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 rounded-full"
                          onClick={() => onQuantityChange?.(itemToControl.productId, itemToControl.productName, itemToControl.rate, Math.max(0, itemToControl.quantity - 1))}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="font-bold min-w-[20px] text-center">{itemToControl.quantity}</span>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 rounded-full"
                          onClick={() => onQuantityChange?.(itemToControl.productId, itemToControl.productName, itemToControl.rate, itemToControl.quantity + 1)}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <>
                  {onBuy && (
                    <Button
                      size="lg"
                      variant="destructive"
                      onClick={onBuy}
                      disabled={isProcessing}
                      className="gap-2 min-h-[48px] flex-1"
                      data-testid="button-buy"
                    >
                      <TrendingDown className="h-5 w-5" />
                      Buy
                    </Button>
                  )}

                  <Button
                    size="lg"
                    onClick={onSell}
                    disabled={isProcessing}
                    className="gap-2 min-h-[48px] flex-1"
                    data-testid="button-sell"
                  >
                    <ShoppingCart className="h-5 w-5" />
                    Sell
                  </Button>
                </>
              )}
            </div>
            {onSellAndShare && !isPro && (
              <Button
                size="sm"
                variant="outline"
                onClick={onSellAndShare}
                disabled={isProcessing}
                className="w-full border-primary text-primary"
              >
                Sell & Share Invoice
              </Button>
            )}
          </div>
        </div>

        {isPro && (
          <Button
            size="lg"
            onClick={onSell}
            disabled={isProcessing}
            className="w-full gap-2 min-h-[48px]"
            data-testid="button-sell-pro"
          >
            <ShoppingCart className="h-5 w-5" />
            Record Sale
          </Button>
        )}
      </div>
    </div>
  );
}
